'use client';

import { motion, useInView, AnimatePresence } from 'motion/react';
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  MessageCircle,
  Search,
  PenTool,
  Palette,
  TestTube,
  ChevronDown,
} from 'lucide-react';
import { isFinnishPage } from '../../../lib/blogPaths';

const STAGES_EN = [
  { icon: MessageCircle, title: 'Initial assessment', description: 'We always start the UX design process by discussing your needs and goals. We define how the application should function and collaboratively set objectives for its development. We can also engage with different stakeholders to identify the role of the application in achieving key business objectives.' },
  { icon: Search, title: 'User research', description: "Through user research, we ensure that the foundations of the application are solid. The research provides valuable insights into the needs, goals, and behaviors of the application's end users. This ensures that the development of the application starts in the right direction, ultimately saving time and money in later stages of development." },
  { icon: PenTool, title: 'Designing the ideal service', description: 'In the design of the application, we use the collected data to create the best possible user experience. At this stage, we define, among other things, the user journeys of the service and its key features. Wireframes, which are simplified versions of the user interface views, help visualize the planned interface and its functionalities before implementation.' },
  { icon: Palette, title: 'UI design', description: "The goal of user interface design is to create a user-friendly and visually appealing interface based on service design principles. In our design process, we consider various factors, including the service's color scheme, typography, icons, and other visual elements. Through responsiveness, we ensure the consistency and usability of the interface across different devices. We use Figma in our design work, which, with its versatile functionalities, also allows us to build interactive demos." },
  { icon: TestTube, title: 'Testing and iteration', description: 'During the testing phase, the user interface and user experience are evaluated in practical scenarios. This may include user testing, A/B testing, and testing the accessibility of the interface. Through testing, we ensure that the designed interface performs as expected across different user groups and usage environments. Based on feedback, we make the necessary changes and improvements before the final release of the service.' },
];

const STAGES_FI = [
  { icon: MessageCircle, title: 'Alkukartoitus', description: 'Aloitamme UX-suunnitteluprosessin aina keskustelemalla tarpeistanne ja tavoitteistanne. Määrittelemme, miten sovelluksen pitäisi toimia ja asetamme yhdessä tavoitteet sen kehittämiseksi. Voimme keskustella myös eri sidosryhmien kanssa, jotta tunnistamme sovelluksen roolin keskeisten liiketoiminnallisten tavoitteiden saavuttamisessa.' },
  { icon: Search, title: 'Käyttäjätutkimus', description: 'Käyttäjätutkimuksen avulla varmistamme, että sovelluksen perustukset ovat kunnossa. Tutkimus tarjoaa arvokasta tietoa sovelluksen loppukäyttäjien tarpeista, tavoitteista ja käyttäytymisestä. Sen kautta varmistamme, että sovellusta lähdetään kehittämään oikeaan suuntaan ja siten säästämme aikaa ja rahaa myöhemmissä kehitysvaiheissa.' },
  { icon: PenTool, title: 'Ideaalin palvelun suunnittelu', description: 'Sovelluksen suunnittelussa käytämme kerättyä tietoa parhaan mahdollisen käyttäjäkokemuksen aikaansaamiseksi. Tässä vaiheessa määrittelemme mm. palvelun käyttäjäpolkuja ja tärkeimpiä ominaisuuksia. Rautalankamallit eli yksinkertaistetut versiot käyttöliittymän näkymistä auttavat hahmottamaan suunnitellun käyttöliittymän ja sen toiminnallisuudet ennen toteutusta.' },
  { icon: Palette, title: 'UI-suunnittelu', description: 'Käyttöliittymäsuunnittelun tavoitteena on luoda käyttäjäystävällinen ja houkutteleva käyttöliittymä palvelumuotoilun pohjalta. Suunnittelussa huomioimme muun muassa palvelun värimaailman, typografian, kuvakkeet ja muut visuaaliset elementit. Responsiivisuuden avulla varmistamme käyttöliittymän yhdenmukaisuuden ja helppokäyttöisyyden eri laitteilla. Käytämme suunnittelutyössä Figmaa.' },
  { icon: TestTube, title: 'Testaus ja iterointi', description: 'Testausvaiheessa käyttöliittymää ja käyttäjäkokemusta arvioidaan käytännön toiminnassa. Tämä voi sisältää esimerkiksi käyttäjätestejä, A/B-testausta ja käyttöliittymän saavutettavuuden testausta. Testauksen kautta varmistamme, että suunniteltu käyttöliittymä toimii odotetusti erilaisissa käyttäjäryhmissä ja käyttöympäristöissä. Palautteen avulla teemme tarvittavat muutokset ja parannukset ennen palvelun lopullista julkaisua.' },
];

export function UiUxDesignProcessSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [openIndex, setOpenIndex] = useState(0);
  const isFinnish = isFinnishPage(router?.pathname);
  const stages = isFinnish ? STAGES_FI : STAGES_EN;
  const heading = isFinnish ? 'Tukea ja ammattitaitoa UX- ja UI-suunnittelun jokaiseen vaiheeseen' : 'Support and expertise in every stage of UX and UI design';

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
        >
          {stages.map((item, index) => {
            const Icon = item.icon;
            const isOpen = openIndex === index;
            return (
              <div
                key={item.title}
                className={`border-b border-gray-100 last:border-b-0 ${isOpen ? 'bg-[#fafbff]' : 'bg-white hover:bg-gray-50/80'}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 rounded-none"
                  aria-expanded={isOpen}
                  aria-controls={`uiux-stage-panel-${index}`}
                  id={`uiux-stage-trigger-${index}`}
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#e2e8f0] text-[#0f172a] shrink-0">
                    <Icon className="w-5 h-5" aria-hidden />
                  </span>
                  <span className="flex-1 font-semibold text-gray-900">{item.title}</span>
                  <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="shrink-0 text-gray-400"
                  >
                    <ChevronDown className="w-5 h-5" aria-hidden />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`uiux-stage-panel-${index}`}
                      role="region"
                      aria-labelledby={`uiux-stage-trigger-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 pt-0 pl-14 sm:pl-14">
                        <p className="text-[15px] text-gray-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
